import React from "react";
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useProjects } from "../../../context/ProjectsContext";
import { isSuperAdmin } from "../../../utils/roles";
import { isIntensiveProjectOnboarding } from "../../../utils/projectExperience";
import {
  loadProjectNavigationState,
  markProjectNavigationAreaExplored,
  persistProjectNavigationExploration,
  projectNavigationAreaExplored,
  projectNavigationKey,
  setProjectNavigationExpanded,
  splitProjectNavigationAreas,
  usesProgressiveProjectNavigation,
} from "../../../utils/progressiveNavigation";
import {
  DASHBOARD_AREA_KEYS,
  resolveDashboardAreaNavigationTarget,
  resolveDashboardNavigation,
  resolveDashboardPrimaryAreas,
  resolveDashboardSecondaryNavigation,
  resolveDashboardToolNavigationTarget,
} from "../../../utils/dashboardNavigation";
import logo from "../../../images/LogoNavbar1.svg";
import BillingAccessBanner from "../BillingAccessBanner";
import { billingAccessActionLabel, billingNavigationStatus } from "../../../utils/projectBillingAccess";
import DashboardNavigationIcon from "../DashboardNavigationIcon";
import DashboardSecondaryNavigation from "../DashboardSecondaryNavigation";
import DashboardTopGlobalControls from "../DashboardTopGlobalControls";
import {
  WEBPROFIT_RESPONSIVE,
  compactNavigationToolButtonSx,
  dashboardContentContainerSx,
  dashboardFormScopeSx,
  dashboardMobileScopeSx,
  responsiveActionsSx,
  responsiveBottomDrawerPaperSx,
} from "../../../theme/webProfitResponsive";

const BRAND = { orange: "#F35A3A", dark: "#19242F", bg: "#F6F7FB" };
const MORE_NAV_VALUE = "__navigation_more__";
const MOBILE_HEADER_HEIGHT = 56;
const TABLET_HEADER_HEIGHT = 64;
const COMPACT_SECONDARY_ROW_HEIGHT = 48;

function pickBottomAreas(areas, activeAreaKey, limit) {
  const candidates = (areas || []).filter((area) => area.key !== DASHBOARD_AREA_KEYS.MORE);
  const selected = candidates.slice(0, Math.max(1, limit));
  const activeArea = candidates.find((area) => area.key === activeAreaKey) || null;

  if (activeArea && !selected.some((area) => area.key === activeArea.key)) {
    selected[selected.length - 1] = activeArea;
  }

  const seen = new Set();
  return selected.filter((area) => {
    if (!area || seen.has(area.key)) return false;
    seen.add(area.key);
    return true;
  });
}

function sheetAreaButtonSx(active, { secondary = false, recommended = false } = {}) {
  return {
    position: "relative",
    minHeight: 66,
    px: 1,
    py: 1,
    borderRadius: 2.5,
    textTransform: "none",
    display: "flex",
    flexDirection: "column",
    gap: .45,
    color: active ? BRAND.orange : secondary ? "rgba(25,36,47,.58)" : BRAND.dark,
    bgcolor: active
      ? "rgba(243,90,58,.09)"
      : secondary
        ? "rgba(25,36,47,.022)"
        : "rgba(25,36,47,.035)",
    border: active
      ? "1px solid rgba(243,90,58,.2)"
      : recommended
        ? "1px solid rgba(243,90,58,.18)"
        : "1px solid rgba(25,36,47,.06)",
    opacity: secondary && !active ? .82 : 1,
    fontSize: ".72rem",
    fontWeight: active || recommended ? 950 : 850,
    "&:hover": {
      bgcolor: active ? "rgba(243,90,58,.12)" : "rgba(25,36,47,.06)",
      opacity: 1,
    },
    "&.Mui-focusVisible": {
      outline: "3px solid rgba(243,90,58,.2)",
      outlineOffset: 1,
    },
  };
}

export default function MobileDashboardShell({ projectOverride }) {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up("sm"));
  const [navigationSheetOpen, setNavigationSheetOpen] = React.useState(false);
  const { user } = useAuth();
  const { projects, activeProject: contextActiveProject, setActiveProject } = useProjects();
  const activeProject = projectOverride !== undefined ? projectOverride : contextActiveProject;
  const location = useLocation();
  const navigate = useNavigate();
  const superAdmin = isSuperAdmin(user);
  const navigation = resolveDashboardNavigation({
    project: activeProject,
    superAdmin,
    pathname: location.pathname,
    search: location.search,
  });
  const projectNavigation = navigation.project;
  const globalNavigation = navigation.global;
  const projectKey = projectNavigationKey(activeProject);
  const userKey = String(user?.id || user?._id || user?.email || "").trim().toLowerCase();
  const [progressiveState, setProgressiveState] = React.useState(() =>
    loadProjectNavigationState(activeProject, user),
  );
  const resolvedPrimaryAreas = React.useMemo(
    () => resolveDashboardPrimaryAreas(navigation),
    [navigation],
  );
  const progressiveEnabled = Boolean(
    navigation.route.isProject &&
      usesProgressiveProjectNavigation(activeProject, { superAdmin }),
  );
  const progressiveAreas = React.useMemo(
    () => splitProjectNavigationAreas(
      activeProject,
      resolvedPrimaryAreas,
      progressiveState,
      progressiveEnabled,
    ),
    [activeProject, resolvedPrimaryAreas, progressiveState, progressiveEnabled],
  );
  const neededAreaKeys = React.useMemo(
    () => new Set(progressiveAreas.neededAreaKeys || []),
    [progressiveAreas.neededAreaKeys],
  );
  const primaryAreas = progressiveAreas.primary;
  const secondaryAreas = progressiveEnabled ? progressiveAreas.secondary : [];
  const sheetPrimaryAreas = React.useMemo(
    () => navigation.route.isProject
      ? primaryAreas.filter((area) => area.key !== DASHBOARD_AREA_KEYS.MORE)
      : resolvedPrimaryAreas,
    [primaryAreas, resolvedPrimaryAreas, navigation.route.isProject],
  );
  const sheetSecondaryAreas = React.useMemo(
    () => secondaryAreas.filter((area) => area.key !== DASHBOARD_AREA_KEYS.MORE),
    [secondaryAreas],
  );
  const supplementalProjectTools = React.useMemo(() => {
    if (!navigation.route.isProject) return [];
    const moreArea = projectNavigation.areas?.find((area) => area.key === DASHBOARD_AREA_KEYS.MORE);
    return (moreArea?.tools || []).filter((tool) => tool.visible !== false);
  }, [navigation.route.isProject, projectNavigation.areas]);
  const moreAreaPromoted = primaryAreas.some((area) => area.key === DASHBOARD_AREA_KEYS.MORE);
  const showSupplementalProjectTools = supplementalProjectTools.length > 0 && (
    !progressiveEnabled || progressiveAreas.expanded || moreAreaPromoted
  );
  const hasProgressiveSecondary = progressiveEnabled && secondaryAreas.length > 0;
  const secondaryNavigation = React.useMemo(
    () => resolveDashboardSecondaryNavigation(navigation),
    [navigation],
  );
  const hasSecondaryNavigation = Boolean(secondaryNavigation.area && secondaryNavigation.tools.length);
  const showSheetSecondaryNavigation = hasSecondaryNavigation
    && secondaryNavigation.area?.key !== DASHBOARD_AREA_KEYS.MORE;
  const secondaryNavigationHeight = hasSecondaryNavigation
    ? COMPACT_SECONDARY_ROW_HEIGHT
    : 0;
  const { modules, billingAccess, projectBase } = projectNavigation;
  const projectRouteActive = navigation.route.isProject;
  const billingStatus = projectRouteActive && !superAdmin
    ? billingNavigationStatus(billingAccess)
    : null;
  const intensiveOnboarding = !superAdmin && isIntensiveProjectOnboarding(activeProject);
  const bottomAreas = React.useMemo(
    () => pickBottomAreas(primaryAreas, navigation.activeAreaKey, isTablet ? 5 : 4),
    [primaryAreas, navigation.activeAreaKey, isTablet],
  );
  const currentBottomValue = bottomAreas.some((area) => area.key === navigation.activeAreaKey)
    ? navigation.activeAreaKey
    : MORE_NAV_VALUE;
  const globalAccountSheetTools = React.useMemo(() => {
    const accountTools = navigation.hasActiveProject
      ? globalNavigation.accountTools
      : globalNavigation.accountTools.filter((tool) => ["projects", "referrals"].includes(tool.key));

    return [
      ...globalNavigation.workspaceTools,
      ...accountTools,
    ];
  }, [navigation.hasActiveProject, globalNavigation.workspaceTools, globalNavigation.accountTools]);
  const globalAdministrationSheetTools = globalNavigation.administrationTools;

  React.useEffect(() => {
    setProgressiveState(loadProjectNavigationState(activeProject, user));
  }, [projectKey, userKey, activeProject, user]);

  React.useEffect(() => {
    const activeAreaKey = navigation.activeAreaKey;
    if (
      !progressiveEnabled ||
      !activeAreaKey ||
      projectNavigationAreaExplored(progressiveState, activeAreaKey)
    ) return;

    setProgressiveState((current) =>
      markProjectNavigationAreaExplored(activeProject, activeAreaKey, current, user),
    );
    persistProjectNavigationExploration(activeProject, { areaKey: activeAreaKey }).catch(() => {});
  }, [
    progressiveEnabled,
    projectKey,
    activeProject,
    user,
    navigation.activeAreaKey,
    progressiveState,
  ]);

  const go = React.useCallback((path) => {
    setNavigationSheetOpen(false);
    navigate(path);
  }, [navigate]);

  const goArea = React.useCallback((area) => {
    const target = resolveDashboardAreaNavigationTarget(navigation, area, location);
    go(target);
  }, [go, navigation, location]);

  const toggleProgressiveTools = React.useCallback(() => {
    setProgressiveState((current) =>
      setProjectNavigationExpanded(activeProject, !progressiveAreas.expanded, current, user),
    );
  }, [activeProject, progressiveAreas.expanded, user]);

  const goTool = React.useCallback((tool) => {
    const target = resolveDashboardToolNavigationTarget(navigation, tool, location);
    go(target);
  }, [go, navigation, location]);

  const renderSheetArea = (area, { secondary = false } = {}) => {
    const recommended = neededAreaKeys.has(area.key);
    return (
      <Button
        key={area.key}
        type="button"
        aria-current={area.active ? "page" : undefined}
        aria-label={`${area.label}${area.locked ? `, ${area.lockLabel || "temporalmente bloqueada"}` : ""}${recommended ? ", recomendado para tu puesta en marcha" : ""}`}
        data-navigation-priority={recommended ? "guided" : (secondary ? "secondary" : "primary")}
        onClick={() => goArea(area)}
        sx={sheetAreaButtonSx(area.active, { secondary, recommended })}
      >
        {area.locked ? (
          <LockClockRoundedIcon sx={{ fontSize: 22 }} />
        ) : (
          <DashboardNavigationIcon name={area.iconKey} sx={{ fontSize: 22 }} />
        )}
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
          {area.label}
        </Box>
        {recommended && !area.active ? (
          <Box
            component="span"
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: 7,
              right: 8,
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: BRAND.orange,
              boxShadow: "0 0 0 2px #fff",
            }}
          />
        ) : null}
      </Button>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: BRAND.bg }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,.97)",
          color: BRAND.dark,
          pt: WEBPROFIT_RESPONSIVE.safeTop,
          borderBottom: "1px solid rgba(25,36,47,.08)",
          backdropFilter: "blur(12px)",
          width: "100%",
          maxWidth: "100vw",
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: `${MOBILE_HEADER_HEIGHT}px !important`, sm: `${TABLET_HEADER_HEIGHT}px !important` },
            pl: { xs: `max(6px, ${WEBPROFIT_RESPONSIVE.safeLeft})`, sm: `max(12px, ${WEBPROFIT_RESPONSIVE.safeLeft})` },
            pr: { xs: `max(6px, ${WEBPROFIT_RESPONSIVE.safeRight})`, sm: `max(12px, ${WEBPROFIT_RESPONSIVE.safeRight})` },
            gap: { xs: .2, sm: .5 },
            minWidth: 0,
          }}
        >
          <IconButton
            size="small"
            aria-label="Abrir navegación"
            aria-expanded={navigationSheetOpen ? "true" : undefined}
            onClick={() => setNavigationSheetOpen(true)}
            sx={{ width: WEBPROFIT_RESPONSIVE.touchTarget, height: WEBPROFIT_RESPONSIVE.touchTarget, flex: "0 0 auto" }}
          >
            <MenuRoundedIcon />
          </IconButton>

          <Box
            component="img"
            src={logo}
            alt="WebProfit"
            sx={{
              width: { xs: 26, sm: 30 },
              height: { xs: 26, sm: 30 },
              objectFit: "contain",
              mx: { xs: .1, sm: .2 },
              flex: "0 0 auto",
            }}
          />

          {billingStatus ? (
            <Chip
              size="small"
              clickable
              label={
                intensiveOnboarding && billingStatus.key === "trial"
                  ? `GUÍA · ${billingAccess.trialDaysRemaining ?? 30}D`
                  : billingStatus.compactLabel
              }
              color={billingStatus.tone === "default" ? undefined : billingStatus.tone}
              variant={billingStatus.tone === "error" || billingStatus.tone === "warning" ? "filled" : "outlined"}
              onClick={() => navigate(
                billingStatus.actionTarget === "billing"
                  ? `${projectBase}/configuracion/suscripcion`
                  : `${projectBase}/configuracion/general`,
              )}
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                maxWidth: 138,
                height: 23,
                fontWeight: 950,
                fontSize: 9.5,
                "& .MuiChip-label": { px: .8, overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          ) : intensiveOnboarding ? (
            <Chip
              size="small"
              label="GUÍA"
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                height: 23,
                bgcolor: "rgba(243,90,58,.10)",
                color: BRAND.orange,
                fontWeight: 950,
                fontSize: 10,
              }}
            />
          ) : null}

          <DashboardTopGlobalControls
            compact
            navigation={navigation}
            project={activeProject}
            projects={projects}
            setActiveProject={setActiveProject}
            contextLabel={navigation.activeArea?.label || "Panel"}
          />
        </Toolbar>

        {hasSecondaryNavigation ? (
          <DashboardSecondaryNavigation
              navigation={navigation}
              project={activeProject}
              superAdmin={superAdmin}
              compact
            />
        ) : null}
      </AppBar>

      <Toolbar
        sx={{
          minHeight: {
            xs: `calc(${MOBILE_HEADER_HEIGHT}px + ${WEBPROFIT_RESPONSIVE.safeTop}) !important`,
            sm: `calc(${TABLET_HEADER_HEIGHT}px + ${WEBPROFIT_RESPONSIVE.safeTop}) !important`,
          },
        }}
      />
      {hasSecondaryNavigation ? <Box sx={{ height: secondaryNavigationHeight }} aria-hidden="true" /> : null}

      <Drawer
        anchor="bottom"
        open={navigationSheetOpen}
        onClose={() => setNavigationSheetOpen(false)}
        PaperProps={{
          sx: responsiveBottomDrawerPaperSx,
        }}
      >
        <Box
          sx={{
            pl: { xs: `max(12px, ${WEBPROFIT_RESPONSIVE.safeLeft})`, sm: 2.25 },
            pr: { xs: `max(12px, ${WEBPROFIT_RESPONSIVE.safeRight})`, sm: 2.25 },
            pt: .75,
            pb: `max(18px, ${WEBPROFIT_RESPONSIVE.safeBottom})`,
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Box sx={{ width: 42, height: 4, borderRadius: 999, bgcolor: "rgba(25,36,47,.16)", mx: "auto", mb: .6 }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Navegación</Typography>
              <Typography variant="caption" noWrap sx={{ display: "block", color: "rgba(25,36,47,.54)", fontWeight: 750 }}>
                {activeProject?.name || "Cuenta WebProfit"}
              </Typography>
            </Box>
            <IconButton aria-label="Cerrar navegación" onClick={() => setNavigationSheetOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <Box
            component="nav"
            aria-label="Áreas del panel"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
              gap: .8,
            }}
          >
            {sheetPrimaryAreas.map((area) => renderSheetArea(area))}
          </Box>

          {hasProgressiveSecondary ? (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Button
                type="button"
                fullWidth
                variant="text"
                aria-expanded={progressiveAreas.expanded ? "true" : "false"}
                onClick={toggleProgressiveTools}
                startIcon={progressiveAreas.expanded
                  ? <ExpandLessRoundedIcon />
                  : <MoreHorizRoundedIcon />}
                sx={{
                  justifyContent: "flex-start",
                  px: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  color: "rgba(25,36,47,.66)",
                  fontWeight: 950,
                }}
              >
                {progressiveAreas.expanded ? "Ocultar herramientas" : "Más herramientas"}
              </Button>

              {progressiveAreas.expanded && sheetSecondaryAreas.length ? (
                <Box
                  component="nav"
                  aria-label="Herramientas secundarias del proyecto"
                  sx={{
                    mt: .8,
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
                    gap: .8,
                  }}
                >
                  {sheetSecondaryAreas.map((area) => renderSheetArea(area, { secondary: true }))}
                </Box>
              ) : null}
            </>
          ) : null}

          {showSheetSecondaryNavigation ? (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" sx={{ display: "block", mb: .8, color: "rgba(25,36,47,.5)", fontWeight: 950, letterSpacing: .45 }}>
                HERRAMIENTAS DE {secondaryNavigation.area.label.toUpperCase()}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={.7}>
                {secondaryNavigation.tools.map((tool) => {
                  const locked = Boolean(tool.locked || tool.restricted);
                  return (
                    <Button
                      key={tool.key}
                      type="button"
                      size="small"
                      aria-current={tool.active ? "page" : undefined}
                      aria-label={`${tool.label}${locked ? `, ${tool.lockLabel || "temporalmente bloqueada"}` : ""}`}
                      onClick={() => goTool(tool)}
                      startIcon={locked
                        ? <LockClockRoundedIcon sx={{ fontSize: "18px !important" }} />
                        : <DashboardNavigationIcon name={tool.iconKey} sx={{ fontSize: "18px !important" }} />}
                      sx={{
                        ...compactNavigationToolButtonSx,
                        color: tool.active ? BRAND.orange : BRAND.dark,
                        bgcolor: tool.active ? "rgba(243,90,58,.09)" : "rgba(25,36,47,.035)",
                        border: tool.active ? "1px solid rgba(243,90,58,.2)" : "1px solid rgba(25,36,47,.06)",
                        fontSize: ".75rem",
                        fontWeight: tool.active ? 950 : 850,
                      }}
                    >
                      {tool.mobileLabel || tool.shortLabel || tool.label}
                    </Button>
                  );
                })}
              </Stack>
            </>
          ) : null}

          {showSupplementalProjectTools ? (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" sx={{ display: "block", mb: .8, color: "rgba(25,36,47,.5)", fontWeight: 950, letterSpacing: .45 }}>
                MÁS
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={.7}>
                {supplementalProjectTools.map((tool) => {
                  const locked = Boolean(tool.locked || tool.restricted);
                  return (
                    <Button
                      key={tool.key}
                      type="button"
                      size="small"
                      aria-current={tool.active ? "page" : undefined}
                      aria-label={`${tool.label}${locked ? `, ${tool.lockLabel || "temporalmente bloqueada"}` : ""}`}
                      onClick={() => goTool(tool)}
                      startIcon={locked
                        ? <LockClockRoundedIcon sx={{ fontSize: "18px !important" }} />
                        : <DashboardNavigationIcon name={tool.iconKey} sx={{ fontSize: "18px !important" }} />}
                      sx={{
                        ...compactNavigationToolButtonSx,
                        color: tool.active ? BRAND.orange : BRAND.dark,
                        bgcolor: tool.active ? "rgba(243,90,58,.09)" : "rgba(25,36,47,.035)",
                        border: tool.active ? "1px solid rgba(243,90,58,.2)" : "1px solid rgba(25,36,47,.06)",
                        fontSize: ".75rem",
                        fontWeight: tool.active ? 950 : 850,
                      }}
                    >
                      {tool.mobileLabel || tool.shortLabel || tool.label}
                    </Button>
                  );
                })}
              </Stack>
            </>
          ) : null}

          {globalAccountSheetTools.length ? (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" sx={{ display: "block", mb: .8, color: "rgba(25,36,47,.5)", fontWeight: 950, letterSpacing: .45 }}>
                {navigation.hasActiveProject ? "CUENTA Y PROYECTOS" : "EMPEZAR"}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={.7}>
                {globalAccountSheetTools.map((tool) => (
                  <Button
                    key={tool.key}
                    type="button"
                    size="small"
                    aria-current={tool.active ? "page" : undefined}
                    onClick={() => goTool(tool)}
                    startIcon={<DashboardNavigationIcon name={tool.iconKey} sx={{ fontSize: "18px !important" }} />}
                    sx={{
                      ...compactNavigationToolButtonSx,
                      color: tool.active ? BRAND.orange : BRAND.dark,
                      bgcolor: tool.active ? "rgba(243,90,58,.09)" : "rgba(25,36,47,.035)",
                      border: tool.active ? "1px solid rgba(243,90,58,.2)" : "1px solid rgba(25,36,47,.06)",
                      fontSize: ".75rem",
                      fontWeight: tool.active ? 950 : 850,
                    }}
                  >
                    {tool.mobileLabel || tool.shortLabel || tool.label}
                  </Button>
                ))}
              </Stack>
            </>
          ) : null}

          {globalAdministrationSheetTools.length ? (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" sx={{ display: "block", mb: .8, color: "rgba(25,36,47,.5)", fontWeight: 950, letterSpacing: .45 }}>
                SUPERADMINISTRACIÓN
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={.7}>
                {globalAdministrationSheetTools.map((tool) => (
                  <Button
                    key={tool.key}
                    type="button"
                    size="small"
                    aria-current={tool.active ? "page" : undefined}
                    onClick={() => goTool(tool)}
                    startIcon={<DashboardNavigationIcon name={tool.iconKey} sx={{ fontSize: "18px !important" }} />}
                    sx={{
                      ...compactNavigationToolButtonSx,
                      color: tool.active ? BRAND.orange : BRAND.dark,
                      bgcolor: tool.active ? "rgba(243,90,58,.09)" : "rgba(25,36,47,.035)",
                      border: tool.active ? "1px solid rgba(243,90,58,.2)" : "1px solid rgba(25,36,47,.06)",
                      fontSize: ".75rem",
                      fontWeight: tool.active ? 950 : 850,
                    }}
                  >
                    {tool.mobileLabel || tool.shortLabel || tool.label}
                  </Button>
                ))}
              </Stack>
            </>
          ) : null}

          {activeProject && (billingAccess.needsPaymentAction || modules.landing) ? (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Stack spacing={.75} sx={responsiveActionsSx}>
                {billingAccess.needsPaymentAction ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<WorkspacePremiumRoundedIcon />}
                    onClick={() => go(`${projectBase}/configuracion/suscripcion`)}
                    sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 900, borderRadius: 2 }}
                  >
                    {billingAccessActionLabel(billingAccess)}
                  </Button>
                ) : null}
                {modules.landing && billingAccess.landingAvailable ? (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PublicRoundedIcon />}
                    onClick={() => go("/")}
                    sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 900, borderRadius: 2 }}
                  >
                    Ver landing
                  </Button>
                ) : null}
              </Stack>
            </>
          ) : null}
        </Box>
      </Drawer>

      <Box
        component="main"
        className="wp-mobile-dashboard"
        sx={{
          pb: `calc(84px + ${WEBPROFIT_RESPONSIVE.safeBottom})`,
          pt: { xs: 1.1, sm: 1.5 },
          ...dashboardFormScopeSx,
          ...dashboardMobileScopeSx,
        }}
      >
        <Container maxWidth={false} disableGutters sx={dashboardContentContainerSx}>
          {navigation.showProjectBillingContext ? <BillingAccessBanner project={activeProject} /> : null}
          <Outlet />
        </Container>
      </Box>

      <Paper
        elevation={10}
        sx={{
          position: "fixed",
          left: { xs: WEBPROFIT_RESPONSIVE.safeLeft, sm: `max(16px, ${WEBPROFIT_RESPONSIVE.safeLeft})` },
          right: { xs: WEBPROFIT_RESPONSIVE.safeRight, sm: `max(16px, ${WEBPROFIT_RESPONSIVE.safeRight})` },
          bottom: { xs: 0, sm: 12 },
          zIndex: 1190,
          maxWidth: { sm: 760 },
          mx: "auto",
          borderRadius: { xs: "16px 16px 0 0 !important", sm: "18px !important" },
          overflow: "hidden",
          border: "1px solid rgba(25,36,47,.08)",
          pb: WEBPROFIT_RESPONSIVE.safeBottom,
        }}
      >
        <BottomNavigation
          value={currentBottomValue}
          onChange={(_event, value) => {
            if (value === MORE_NAV_VALUE) {
              setNavigationSheetOpen(true);
              return;
            }
            const area = bottomAreas.find((candidate) => candidate.key === value);
            if (area) goArea(area);
          }}
          showLabels
          sx={{
            height: { xs: 64, sm: 68 },
            "& .MuiBottomNavigationAction-root": {
              minWidth: 0,
              px: { xs: .2, sm: .7 },
              color: "rgba(25,36,47,.55)",
            },
            "& .Mui-selected": { color: BRAND.orange },
            "& .MuiBottomNavigationAction-label": { fontSize: { xs: ".63rem", sm: ".7rem" }, fontWeight: 850 },
            "& .MuiBottomNavigationAction-label.Mui-selected": { fontSize: { xs: ".65rem", sm: ".72rem" } },
          }}
        >
          {bottomAreas.map((area) => (
            <BottomNavigationAction
              key={area.key}
              value={area.key}
              label={area.label}
              aria-label={`Ir a ${area.label}`}
              icon={area.locked
                ? <LockClockRoundedIcon />
                : <DashboardNavigationIcon name={area.iconKey} />}
            />
          ))}
          <BottomNavigationAction
            value={MORE_NAV_VALUE}
            label="Más"
            aria-label="Abrir todas las áreas y herramientas"
            icon={<MoreHorizRoundedIcon />}
          />
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
